import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { type ReactElement, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  AccountLimitsVersion,
  ChannelSettings,
  SettingsVersion,
  SubscriptionId,
} from '../../api';
import { isHiddenMode, useFeatures } from '../../lib/config/features-context';

import { ExecutionSection } from './ExecutionSection';
import { HighRiskConfirmSheet } from './HighRiskConfirmSheet';
import { LimitsSection } from './LimitsSection';
import { MissingList } from './MissingList';
import { PrefilledNotice } from './PrefilledNotice';
import { RecommendationBanner } from './RecommendationBanner';
import { SaveOutcome } from './SaveOutcome';
import { SizingSection } from './SizingSection';
import { StopLossSection } from './StopLossSection';
import {
  accountAfterSave,
  type DailyLimitDraft,
  initialDailyLimitDraft,
  type LimitsAccount,
  validateDailyLimitDraft,
} from './daily-limit';
import { type HighRiskConfirm, needsHighRiskConfirm } from './high-risk';
import { isPreviewCurrent } from './risk-preview-state';
import {
  applyRecommendation,
  draftFallbackStopLossPips,
  draftSizing,
  type FormVariant,
  initialDraft,
  sameSettings,
  type SettingsDraft,
  validateSettingsDraft,
} from './settings-draft';
import { useRiskPreview } from './use-risk-preview';
import { useSaveSettings } from './use-save-settings';

/** What a save stored: the new channel version and the new account limit, each when sent. */
export interface SavedSettings {
  /** `null` when the channel settings were unchanged and only the limit was saved. */
  readonly version: SettingsVersion | null;
  /** `null` when the account limit was not touched or failed. */
  readonly limits: AccountLimitsVersion | null;
  /**
   * Set only in the `firstSetup` variant (S7 R18): true when every part sent was stored, false
   * when one was refused (the channel version stored and the limit refused). Absent on E7, whose
   * callers and tests keep the shape they had.
   */
  readonly complete?: boolean;
}

/** Props of {@link ChannelSettingsForm}. */
interface ChannelSettingsFormProps {
  readonly subscriptionId: SubscriptionId;
  /** The saved settings, or `null` for a channel never set up (nothing is then pre-selected, I7). */
  readonly active: ChannelSettings | null;
  /**
   * `version` of the active settings, sent back as `expectedVersion` so a save from a stale screen
   * answers 409 instead of overwriting another device (B-D16); `null` when never configured.
   */
  readonly activeVersion: number | null;
  /** The affiliate's recommendation, offered behind an explicit `[Apply]` (M2 R14). */
  readonly recommended: ChannelSettings | null;
  /** What the account-wide daily-limit card can show (M2 R12). */
  readonly limitsAccount: LimitsAccount;
  /**
   * Open positions on this channel (M2 R15). Not in C1 yet (review decision D3): without it the
   * notice says the sentence that is true whatever the count. With it, the "keep their size"
   * sentence shows only above 0.
   */
  readonly openPositionCount?: number | undefined;
  /**
   * Called after each save that stored something, the channel settings saved but the limit
   * refused included, e.g. to reload the channel status.
   */
  readonly onSaved?: ((saved: SavedSettings) => void) | undefined;
  /**
   * `'firstSetup'` (S7 R18, E5): starts from the affiliate's recommendation when nothing is saved,
   * says so in a note instead of the `[Apply]` banner, and labels the button "Confirm and start
   * copying". Default `'settings'`: E7, unchanged.
   */
  readonly variant?: FormVariant | undefined;
  /** Called with `true` when a save starts and `false` when it ends (S7 R17); E7 omits it. */
  readonly onSavingChange?: ((saving: boolean) => void) | undefined;
}

/**
 * The settings form of one channel (M2 §2): E7 uses it, and S7 reuses it for E5. It owns the
 * draft, the money preview and the save; the caller passes what it read from the API. It reads
 * its props once: a reload of the caller never overwrites what the trader is typing.
 *
 * I7 holds here: nothing is pre-selected without a saved version, the recommendation applies
 * only on a tap, no value is rounded or clamped, and Save needs a preview of the exact size.
 */
export function ChannelSettingsForm({
  subscriptionId,
  active,
  activeVersion,
  recommended,
  limitsAccount,
  openPositionCount,
  onSaved,
  variant = 'settings',
  onSavingChange,
}: ChannelSettingsFormProps): ReactElement {
  const { t } = useTranslation();
  const features = useFeatures();
  const [draft, setDraft] = useState<SettingsDraft>(() =>
    initialDraft(active, recommended, variant, features),
  );
  // S7 R5: E5 started from the recommendation, so it shows the note and no `[Apply]` banner.
  const [prefilledStart] = useState(
    () => variant === 'firstSetup' && active === null && recommended !== null,
  );
  // What is saved now. It moves after each save, so it is state too.
  const [savedSettings, setSavedSettings] = useState<ChannelSettings | null>(active);
  const [savedVersion, setSavedVersion] = useState<number | null>(activeVersion);
  const [account, setAccount] = useState<LimitsAccount>(limitsAccount);
  const [dailyLimit, setDailyLimit] = useState<DailyLimitDraft>(() =>
    initialDailyLimitDraft(limitsAccount),
  );
  const sizing = draftSizing(draft);
  // P9 R6: the preview also gets the fallback distance, so it says what a signal without SL costs.
  const fallbackStopLossPips = draftFallbackStopLossPips(draft);
  const previewRequest = sizing === null ? null : { sizing, fallbackStopLossPips };
  const preview = useRiskPreview(subscriptionId, previewRequest);
  const saving = useSaveSettings(subscriptionId);
  const busy = saving.state.status === 'saving';
  // P11: the high risk the trader is asked to confirm before the save; null when no sheet.
  const [highRisk, setHighRisk] = useState<HighRiskConfirm | null>(null);
  // P11 R9: a double tap can land twice before `busy` re-renders the button; one save only.
  const confirming = useRef(false);
  useEffect(() => {
    onSavingChange?.(busy);
  }, [busy, onSavingChange]);

  const validation = validateSettingsDraft(draft);
  const issues = validation.ok ? [] : validation.issues;
  const limitValidation = validateDailyLimitDraft(dailyLimit, account);
  const previewCurrent = isPreviewCurrent(previewRequest, preview.state);
  // Only what changed is sent: an identical channel version is never created (M2 R16), and the
  // account limit is never rewritten by accident (R12).
  const settingsBody =
    validation.ok && (savedSettings === null || !sameSettings(validation.value, savedSettings))
      ? { ...validation.value, expectedVersion: savedVersion }
      : null;
  const limitsBody =
    limitValidation.ok && limitValidation.change === 'set' ? limitValidation.body : null;
  // P8 R3: the saved version is in a mode the trader cannot choose now, so the draft has no mode.
  const savedModeHidden =
    savedSettings !== null && isHiddenMode(savedSettings.execution.mode, features);
  // P8 R3 with M2 R12: the account-wide daily limit can still be saved alone while the channel
  // is left exactly as saved (only its hidden mode missing from the draft). Any change to the
  // channel itself needs a mode chosen first; no settings version is sent in this case.
  const channelKeptAsSaved =
    savedSettings !== null &&
    savedModeHidden &&
    draft.executionMode === null &&
    keepsSavedChannel(draft, savedSettings);
  const channelValid = validation.ok || channelKeptAsSaved;
  const valid = channelValid && limitValidation.ok;
  const unchanged =
    validation.ok && limitValidation.ok && settingsBody === null && limitsBody === null;
  const canSave =
    valid && (settingsBody !== null || limitsBody !== null) && previewCurrent && !busy;

  const edit = (patch: Partial<SettingsDraft>): void => {
    setDraft((current) => ({ ...current, ...patch }));
    saving.reset();
  };
  const editDailyLimit = (next: DailyLimitDraft): void => {
    setDailyLimit(next);
    saving.reset();
  };

  // P11 R1–R7: Save first asks to confirm a new risk above the threshold (E7 only). The sheet is
  // modal, so the draft cannot change while it is open: Confirm saves exactly what was checked.
  const onSavePressed = (): void => {
    if (!canSave) return;
    const confirm = needsHighRiskConfirm({
      variant,
      sending: settingsBody?.sizing ?? null,
      saved: savedSettings?.sizing ?? null,
      preview: preview.state,
    });
    if (confirm === null) {
      void onSave();
    } else {
      setHighRisk(confirm);
    }
  };

  const onSave = async (): Promise<void> => {
    if (!canSave) return;
    const result = await saving.save(settingsBody, limitsBody);
    if (result.version !== null) {
      setSavedSettings(result.version.settings);
      setSavedVersion(result.version.version);
    }
    if (result.limits !== null) {
      const next = accountAfterSave(account, result.limits);
      setAccount(next);
      setDailyLimit(initialDailyLimitDraft(next));
    }
    // A partial save (settings stored, limit refused) still changed the channel: the caller
    // reloads its status, and the next Save sends only the limit.
    if (result.version !== null || result.limits !== null) {
      onSaved?.({
        version: result.version,
        limits: result.limits,
        ...(variant === 'firstSetup' ? { complete: result.error === null } : {}),
      });
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
      {/* Locked while saving: what is on screen is always what was sent (no "Saved" on a draft
          edited meanwhile). A disabled fieldset disables every control inside it. */}
      <Box
        component="fieldset"
        disabled={busy}
        sx={{
          border: 0,
          p: 0,
          m: 0,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.75,
        }}
      >
        {prefilledStart && savedSettings === null && recommended !== null && (
          <PrefilledNotice recommended={recommended} />
        )}
        {!prefilledStart && recommended !== null && (
          <RecommendationBanner
            recommended={recommended}
            onApply={() => {
              setDraft((current) => applyRecommendation(current, recommended, features));
              saving.reset();
            }}
          />
        )}
        <SizingSection
          draft={draft}
          onChange={edit}
          issues={issues}
          preview={preview.state}
          onRetryPreview={preview.retry}
        />
        <StopLossSection draft={draft} onChange={edit} issues={issues} preview={preview.state} />
        <ExecutionSection draft={draft} onChange={edit} savedModeHidden={savedModeHidden} />
        <LimitsSection
          draft={draft}
          onChange={edit}
          issues={issues}
          account={account}
          dailyLimit={dailyLimit}
          onDailyLimitChange={editDailyLimit}
          dailyLimitInvalid={!limitValidation.ok && limitValidation.problem === 'invalid'}
          dailyLimitLeadingDigit={!limitValidation.ok && limitValidation.leadingDigit === true}
        />
      </Box>
      <SaveOutcome state={saving.state} openPositionCount={openPositionCount} />
      <Button
        variant="contained"
        size="large"
        disabled={!canSave}
        aria-busy={busy}
        onClick={onSavePressed}
        sx={{ borderRadius: '14px' }}
      >
        {busy
          ? t('settings.saving')
          : variant === 'firstSetup'
            ? t('settings.firstSetup.confirm')
            : t('settings.save')}
      </Button>
      {saving.state.status === 'idle' && (
        <MissingList
          issues={canSave ? [] : issues}
          limitValidation={limitValidation}
          preview={preview.state}
          previewCurrent={previewCurrent}
          unchanged={unchanged}
        />
      )}
      <HighRiskConfirmSheet
        confirm={highRisk}
        busy={busy}
        onConfirm={() => {
          // P11 R9 (C3 R11): the sheet stays open (busy) while the save runs, then closes on either
          // outcome.
          if (confirming.current) return;
          confirming.current = true;
          void onSave().finally(() => {
            confirming.current = false;
            setHighRisk(null);
          });
        }}
        onCancel={() => {
          setHighRisk(null);
        }}
      />
    </Box>
  );
}

/**
 * True when the draft, given the saved mode back, is exactly the saved channel (P8 R3): the
 * trader touched nothing on the channel, so a limits-only save stores no settings version.
 */
function keepsSavedChannel(draft: SettingsDraft, saved: ChannelSettings): boolean {
  const check = validateSettingsDraft({ ...draft, executionMode: saved.execution.mode });
  return check.ok && sameSettings(check.value, saved);
}
